export const Shuttle_SOP = {
    vehicleCleanliness: {
        title: "Vehicle Cleanliness",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/vehicleCleanlinessTitle.png",
        body: `
          <section>
            <h3>🧼 Clean Van, Calm Ride</h3>
            <p>
              A clean shuttle vehicle isn’t just about appearance — it’s about <span class="highlight-green">safety, comfort, and professional care</span>. 
              Pawsible vans should always be <span class="highlight">odor-free, hair-free, and sanitized</span> before and after each route.
            </p>
          </section>
      
          <section>
            <h4>🧽 1. Daily Cleaning Checklist</h4>
            <ul>
              <li>🧹 <strong>Before first pickup:</strong> Sweep floor, remove debris or shed fur</li>
              <li>🧼 Wipe down all seats with pet-safe disinfectant</li>
              <li>🚿 Clean up any liners or replace with fresh pee pads if used</li>
              <li>🌬️ Ensure proper ventilation and deodorize if needed</li>
            </ul>
            <p><span class="highlight-blue">First impressions begin at the vehicle door — don’t let messes undermine trust.</span></p>
          </section>
      
          <section>
            <h4>🧺 2. Post-Route End-of-Day Clean</h4>
            <ul>
              <li>🧽 Remove all soiled pads, trash, and fur buildup</li>
              <li>🧴 Sanitize seats and floors</li>
              <li>🧹 Sweep thoroughly — check under seats and corners</li>
              <li>🧼 Spot clean any visible drool, dirt, or accidents</li>
              <li>🚫 Never leave food, treats, or soiled items in van overnight</li>
            </ul>
            <p><span class="highlight-green">End-of-day resets ensure the next morning starts fresh, hygienic, and stress-free for dogs and humans.</span></p>
          </section>
      
          <section>
            <h4>🚨 3. Special Cases & Incident Cleanup</h4>
            <ul>
              <li>💩 If a dog has an accident, pull over safely and clean before continuing</li>
              <li>🧴 Use designated spill kits or enzymatic cleaner from the supply bin</li>
              <li>📸 Optional: Document and report severe messes or damaged crate equipment</li>
            </ul>
            <p><span class="highlight-red">Never ignore a mess — it can cause discomfort, health risk, and client concern.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Clean spaces = calm minds.</span>  
              Our shuttle vans reflect our standard of care. Clean like every client is watching — because in a way, they are.
            </p>
          </section>
        `
      },
      preDepartureChecklist: {
        title: "Pre-Departure Safety Checklist",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/preDepartureChecklistTitle.png",
        body: `
          <section>
            <h3>🛡️ Start Safe, Arrive Safer</h3>
            <p>
              Every shuttle journey at Pawsible begins with <span class="highlight-green">intentional safety and readiness checks</span>. 
              This routine ensures <span class="highlight">a smooth ride, secure crate setup, and confident handoffs</span> for every dog and every driver.
            </p>
          </section>
      
          <section>
            <h4>📋 1. Vehicle Readiness</h4>
            <ul>
              <li>🧹 Confirm vehicle is clean, organized, and odor-free</li>
              <li>🔒 Check all safety equipments are secured and properly latched</li>
              <li>🧼 Ensure clean pee pads, blankets, or mats are in place</li>
              <li>🛢️ Verify fuel level is sufficient for entire route</li>
              <li>🔋 Make sure emergency kit and cleaning supplies are onboard</li>
            </ul>
            <p><span class="highlight-blue">Safety starts before ignition — a well-prepped van means fewer issues on the road.</span></p>
          </section>
      
          <section>
            <h4>🐾 2. Dog Manifest Review</h4>
            <ul>
              <li>📲 Open shuttle route on Pawsible app and review pickup/drop-off schedule</li>
              <li>📋 Confirm seat assignments for size, temperament, and compatibility</li>
              <li>🔍 Check for alerts: reactive dogs, medical notes, anxiety flags</li>
            </ul>
            <p><span class="highlight-green">Know your passengers — preparation reduces stress for both dogs and staff.</span></p>
          </section>
      
          <section>
            <h4>🔐 3. Safety Before Departure</h4>
            <ul>
              <li>🚪 Ensure doors are shut, equipments are double-latched, and all gear is secure</li>
              <li>📲 Notify front desk team that shuttle is departing via internal comms</li>
              <li>📸 Optional: Take a van photo for accountability and route documentation</li>
            </ul>
            <p><span class="highlight-yellow">Treat every route like it’s your first — safety and mindfulness build lasting trust with paw parents.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Shuttle safety is not just logistics — it’s a mindset.</span>  
              When we start right, dogs ride calm and paw parents stay confident.
            </p>
          </section>
        `
      },
      pickupDropoffTiming: {
        title: "Pickup & Drop-Off Timing SOP",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/pickupDropoffTimingTitle.png",
        body: `
          <section>
            <h3>🕒 On Time, Calmly, and With Care</h3>
            <p>
              Pawsible shuttle service relies on <span class="highlight-green">timeliness, efficiency, and low-stress transitions</span>. 
              This SOP ensures every ride is <span class="highlight">predictable for paw parents and peaceful for pets</span>.
            </p>
          </section>
      
          <section>
            <h4>🚐 1. Standard Pickup & Drop-Off Windows</h4>
            <ul>
              <li>🌄 <strong>Morning Pickup Window:</strong> 7:00AM – 9:00AM (Daycare, Boarding, and AM Grooming)</li>
              <li>🌤️ <strong>Midday Service Window:</strong> 10:00AM – 3:00PM (Training, Grooming, Airport Drops, Adventure Days)</li>
              <li>🌆 <strong>Evening Drop-Off Window:</strong> 4:30PM – 6:00PM (All return trips — daycare, training, and boarding)</li>
              <li>🗓️ Follow optimized route order from the Pawsible app and update in real-time</li>
            </ul>
            <p><span class="highlight-blue">Predictability reduces pet anxiety and builds trust with paw parents.</span></p>
          </section>
      
          <section>
            <h4>📲 2. Communication Expectations</h4>
            <ul>
              <li>📲 <span class="highlight-red">Notify clients via the Pawsible App 10 minutes prior to arrival</span> to ensure they’re ready for timely handoff</li>
              <li>📍 Check off each successful pickup/drop-off in the app as you go</li>
              <li>📸 Optional: Photo at drop-off for new or anxious clients to reassure paw parents</li>
              <li>📩 If running more than 10 minutes behind, notify front desk to alert clients</li>
            </ul>
            <p><span class="highlight-green">Every update adds reassurance — transparency earns trust.</span></p>
          </section>

          <section>
            <h4>⏳ 3. Late Client Protocol</h4>
            <ul>
                <li>🕔 Drivers will wait up to <span class="highlight-red">5 minutes maximum</span> for any daycare stop</li>
                <li>🚐 If client is not present or ready, proceed with next pickup to stay on schedule</li>
                <li>📲 Notify the front desk immediately and mark the stop as "missed" in the Pawsible app</li>
                <li>📩 Follow up with the client to reschedule or confirm return window</li>
                <li>🔁 <span class="highlight-green">Return to the zone after completing the next/rest of the route</span> — reattempt pickup only if route timing allows</li>
            </ul>
            <p><span class="highlight-blue">Maintaining route flow ensures fairness to all clients and avoids stress for waiting pets.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Shuttle time is bonding time.</span> 
              Every pickup and drop-off is a small but powerful touchpoint in the full-service Pawsible experience.
            </p>
          </section>
        `
      },
      noShowDelayProtocol: {
        title: "No-Show or Delay Protocol",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/noShowDelayProtocolTitle.png",
        body: `
          <section>
            <h3>⏰ When Plans Shift: Handling Delays and No-Shows Professionally</h3>
            <p>
              In shuttle operations, <span class="highlight">delays and no-shows must be handled smoothly and fairly</span>. 
              Our goal is to <span class="highlight-green">minimize disruption for other clients while maintaining excellent communication and boundaries</span>.
            </p>
          </section>
      
          <section>
            <h4>🚫 1. Client No-Show Protocol</h4>
            <ul>
              <li>🕐 Wait up to <span class="highlight-yellow">5 minutes maximum</span> at the scheduled stop</li>
              <li>📲 If client is not at door, <strong>attempt one contact via text or call</strong> using the Pawsible Driver Task Board contact info</li>
              <li>🚐 If no response, <span class="highlight-blue">leave and proceed to the next stop</span>. Do not wait longer</li>
              <li>📩 Mark the attempt as "Missed Pickup" in the app and notify front desk</li>
            </ul>
            <p><span class="highlight-red">Delays impact all dogs — we must respect the route schedule and fairness for all clients.</span></p>
          </section>
      
          <section>
            <h4>🚦 2. Client Delay Requests</h4>
            <ul>
              <li>📍 Delays must be pre-notified by client <span class="highlight-yellow">at least 1 hour before route</span></li>
              <li>📋 Late readiness beyond 5-minute grace may require rescheduling or surcharge if repeat issue</li>
              <li>🗓️ Document pattern delays in the client profile for future route planning</li>
            </ul>
            <p><span class="highlight-blue">Clear policies help clients respect our time while maintaining flexibility when appropriate.</span></p>
          </section>
      
          <section>
            <h4>📩 3. Driver Delay or Reroute</h4>
            <ul>
              <li>🕒 If running more than 10 minutes behind, alert front desk so they can notify clients proactively</li>
              <li>📲 Log updated ETA in the task board for real-time clarity</li>
              <li>🛠️ Reroute only with permission from dispatcher or front desk lead</li>
            </ul>
            <p><span class="highlight-green">Transparency builds trust — even small updates can prevent frustration.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">We’re on a schedule — but we’re also in a relationship business.</span>  
              Balancing professionalism with empathy ensures smooth routes, satisfied clients, and happier dogs.
            </p>
          </section>
        `
      },
      crateLoadingSafety: {
      title: "Dog Loading & Seating Safety Procedures",
      image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/crateLoadingSafetyTitle.png",
        body: `
          <section>
            <h3>🔐 Safe Starts: Loading & Seating Procedures for Every Ride</h3>
            <p>
              At Pawsible, shuttle safety begins the moment we approach a dog. <span class="highlight">Calm handling and proper crating prevent stress, injuries, and escapes</span> — while showing parents we’re pros.
            </p>
          </section>
      
          <section>
            <h4>🚪 1. Approaching for Pickup</h4>
            <ul>
              <li>🧘 Greet calmly — avoid loud tones or overexcitement</li>
              <li>🪢 Leash the dog securely before exiting their home or facility</li>
              <li>👀 Double-check gear fit: no loose collars or tangled leashes</li>
              <li>🚶 Walk calmly to the shuttle using confident, steady pace</li>
            </ul>
            <p><span class="highlight-blue">First impressions matter — your energy sets the tone for the entire ride.</span></p>
          </section>
      
          <section>
            <h4>📦 2. Pet Loading Procedures</h4>
            <ul>
              <li>🛻 Always open doors fully and guide the dog in with verbal cue or treat</li>
              <li>🔒 Confirm pet is fully latched and secure before walking away</li>
              <li>🎯 Ensure dogs are loaded into correct crate based on manifest size/temperament match</li>
              <li>📋 If a dog resists crating, pause and use treats or patience — never force or drag</li>
            </ul>
            <p><span class="highlight-green">Safety over speed — a calm load-in is worth the extra seconds.</span></p>
          </section>
      
          <section>
            <h4>🚨 3. Seat Safety Rules</h4>
            <ul>
              <li>✅ Seats must be double-latched with no broken or bent parts</li>
              <li>🚫 No loose items, collars with tags, or harnesses left inside vehicle unsupervised</li>
              <li>🧺 Use absorbent bedding or pee pads unless contraindicated for chewing</li>
              <li>⚠️ Never leave more than one dog in a crate unless specifically assigned as bonded pair</li>
            </ul>
            <p><span class="highlight-red">Unsafe crates = unsafe rides. Check every latch, every time.</span></p>
          </section>
      
          <section>
            <h4>📤 4. Drop-Off & Unloading Protocol</h4>
            <ul>
              <li>🪢 Leash dog before taking dogs out</li>
              <li>🧘 Exit calmly, no rushing or crowding at doors</li>
              <li>📲 Confirm handoff or location drop is logged in app</li>
              <li>💬 Notify front desk of any issues during the ride (anxiety, mess, refusal)</li>
            </ul>
            <p><span class="highlight-blue">Smooth unloads prevent last-minute chaos — and leave a positive final impression.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The ride is more than transport — it’s trust in motion.</span>  
              Loading with care, securing crates properly, and respecting each dog's space builds safety and confidence with every route.
            </p>
          </section>
        `
      },
      routeCommunication: {
        title: "Route Optimization & Communication",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/routeCommunicationTitle.png",
        body: `
          <section>
            <h3>🧭 Smart Routes, Smooth Days</h3>
            <p>
              Pawsible shuttle success depends on <span class="highlight-green">efficient routing and clear real-time communication</span>. 
              Whether handling daycare pickups, grooming trips, or adventure days, <span class="highlight">every route should feel seamless and intentional</span>.
            </p>
          </section>
      
          <section>
            <h4>📍 1. Route Planning Before Departure</h4>
            <ul>
              <li>📲 Review the shuttle manifest and zones in the Pawsible driver dashboard</li>
              <li>🗺️ Plan route to minimize drive time and maximize pet comfort</li>
              <li>🚧 Account for traffic trends, construction, and weather alerts before departing</li>
              <li>📝 Reorder stops if needed — but confirm all client time windows and service types are respected</li>
            </ul>
            <p><span class="highlight-blue">A well-planned route saves time, fuel, and stress for everyone involved.</span></p>
          </section>
      
          <section>
            <h4>📲 2. In-Route Communication Protocol</h4>
            <ul>
              <li>📍 Mark each completed stop on the Pawsible app in real-time</li>
              <li>📣 Notify the front desk immediately if delays over 10 minutes occur</li>
              <li>📸 Optional: Send photo updates for new riders or anxious paw parents</li>
              <li>📬 Use preset messages (or quick team updates) if rerouting or issues arise</li>
            </ul>
            <p><span class="highlight-green">Transparency builds trust — parents feel safer when they know where their pup is.</span></p>
          </section>
      
          <section>
            <h4>🔄 3. Post-Route Debrief & Notes</h4>
            <ul>
              <li>📝 Log any changes to pickup/drop-off order in the driver notes</li>
              <li>🐾 Note dogs who were difficult to load, anxious in the crate, or unusually quiet</li>
              <li>🛠️ Suggest future route improvements based on experience (to ops or manager)</li>
              <li>📸 Report new addresses or client setup issues for future updates</li>
            </ul>
            <p><span class="highlight-blue">Small notes today prevent big issues tomorrow — your feedback sharpens every route.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">The best routes feel invisible.</span>  
              When our shuttle operations flow effortlessly, dogs ride calm, parents stay informed, and our team runs like a well-oiled machine.
            </p>
          </section>
        `
      },
      shuttleClientEtiquette: {
        title: "Client Interaction Etiquette (At the Door)",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/shuttleClientEtiquetteTitle.png",
        body: `
          <section>
            <h3>🤝 First Impressions Matter — Even at the Door</h3>
            <p>
              At Pawsible, our shuttle service is more than transportation — it’s a <span class="highlight-green">white-glove experience that reflects our brand</span>. 
              Every front door interaction should <span class="highlight">communicate trust, professionalism, and calm confidence</span>.
            </p>
          </section>
      
          <section>
            <h4>🚪 1. Pickup Interaction Etiquette</h4>
            <ul>
              <li>😊 Greet the client warmly by name (if present) or wave with friendly body language</li>
              <li>🐶 Speak to the dog in a calm, cheerful voice — “Ready for daycare, Luna?”</li>
              <li>🪢 Leash and double-check gear before leaving doorstep</li>
              <li>🔒 Always close gates and doors securely behind you</li>
              <li>📸 Optional: Snap a friendly photo of dog en route for parent reassurance</li>
            </ul>
            <p><span class="highlight-blue">Every pickup should feel like a trusted routine — not a rushed exchange.</span></p>
          </section>
      
          <section>
            <h4>🏡 2. Drop-Off Etiquette</h4>
            <ul>
              <li>🧘 Return dogs calmly — no hype or loud goodbyes that encourage jumping or reactivity</li>
              <li>🎯 Ensure the dog is safely inside or handed off before departing</li>
              <li>📍 Confirm location instructions (e.g., side gate, front porch) are followed exactly</li>
              <li>💬 Leave a verbal note if anything important happened during transport (nervousness, delay, gear issue)</li>
            </ul>
            <p><span class="highlight-green">Depart with the same care you arrived with — quiet, professional, and reliable.</span></p>
          </section>
      
          <section>
            <h4>⚠️ 3. What <span class="highlight-red">Not</span> to Do</h4>
            <ul>
              <li>🚫 Never let dogs off-leash before reaching a secure location</li>
              <li>🚫 Don’t linger for casual chat — stay efficient while being warm</li>
              <li>🚫 Never enter a client’s home unless explicitly instructed</li>
              <li>🚫 Avoid cell phone use during client handoff unless using app for check-in</li>
            </ul>
            <p><span class="highlight-red">Professionalism at the door reinforces the trust families place in us every day.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">We don’t just drive dogs — we represent peace of mind.</span>  
              Each front door moment is a chance to show parents their pup is in expert, loving hands.
            </p>
          </section>
        `
      },
      transportIncidentProtocol: {
        title: "Behavioral Incident Handling During Transport",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/transportIncidentProtocolTitle.png",
        body: `
          <section>
            <h3>🚨 Stay Calm, Stay Safe: Handling Behavioral Issues Onboard</h3>
            <p>
              Even well-mannered dogs can become reactive in transit. At Pawsible, <span class="highlight">our drivers are trained to prevent, de-escalate, and report behavioral incidents</span> with professionalism and calm authority.
            </p>
          </section>
      
          <section>
            <h4>🧠 1. Prevention Starts with Setup</h4>
            <ul>
              <li>📋 Review notes on known triggers or reactive tendencies in the shuttle manifest</li>
              <li>🚐 Load dogs in order of compatibility and crate securely before the next is added</li>
              <li>🛡️ Avoid placing high-anxiety or reactive dogs next to each other</li>
              <li>🔕 Keep noise and stimulation low — avoid loud music or raised voices</li>
            </ul>
            <p><span class="highlight-blue">A calm van layout = fewer chances for conflict.</span></p>
          </section>
      
          <section>
            <h4>⚠️ 2. Signs of Escalation</h4>
                <ul>
                    <li>🐾 Excessive barking, lunging within seat, growling, or digging</li>
                </ul>
                <p><span class="highlight-green">🧘‍♂️ Calmly speak in a low tone. Avoid eye contact, reduce environmental triggers (e.g., music), and create more physical distance between seats if possible.</span></p>

                <ul>
                    <li>💦 Sudden panting, pacing, or visible anxiety</li>
                </ul>
                <p><span class="highlight-green">🌬️ Crack a window or gently increase airflow. Speak softly reassuring the dog. Maintain smooth driving without sudden stops or sharp turns.</span></p>

                <ul>
                    <li>🔁 Dogs redirecting frustration onto the walls or themselves</li>
                </ul>
                <p><span class="highlight-green">🚫 Do not panic — instead, pull over if safe and reposition the dog away from visual stressors. Log concern for follow-up and apply calm verbal redirection.</span></p>

                <ul>
                    <li>🚨 Aggressive outbursts toward nearby dogs</li>
                </ul>
                <p><span class="highlight-green">🛑 Immediately halt interaction by blocking sight or repositioning seats. Skip next pickup if safety is at risk. Alert manager upon return.</span></p>

                <p><span class="highlight-red">If signs of escalation begin, act fast to de-escalate before the situation worsens.</span></p>
            </section>

      
          <section>
            <h4>🛠️ 3. On-the-Road Response Protocol</h4>
            <ul>
              <li>🧘 Lower your voice and pull over safely if the behavior becomes unsafe</li>
              <li>🚫 Do not relocate seats unless there’s an emergency or the dog is harming itself</li>
              <li>📍 Create more distance by adjusting seats if possible</li>
              <li>🛑 If needed, skip the next pickup and return to base for manager support</li>
            </ul>
            <p><span class="highlight-yellow">Your tone and body language can either escalate or calm — stay neutral, not reactive.</span></p>
          </section>
      
          <section>
            <h4>📲 4. Post-Incident Documentation</h4>
            <ul>
              <li>📝 Log the incident immediately in the shuttle report section of the app</li>
              <li>📸 Take photos of any car damage or concerning behavior if safe to do so</li>
              <li>💬 Notify front desk or shift manager once route is safely completed</li>
              <li>📂 Include details: what happened, how it was handled, and what might help next time</li>
            </ul>
            <p><span class="highlight-green">Incident transparency builds team strategy and prevents repeat stressors.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Transport isn’t just movement — it’s part of the emotional experience.</span>  
              Calm, confident handling during behavioral moments keeps everyone safe and preserves the trust placed in us.
            </p>
          </section>
        `
      },
      shuttleEndOfDayRecap: {
        title: "End-of-Day Shuttle Recap & Reporting",
        image: "https://pawsible-s3.s3.us-west-1.amazonaws.com/training_images/guide_images/shuttleEndOfDayRecapTitle.png",
        body: `
          <section>
            <h3>📋 Finish Strong: Daily Shuttle Wrap-Up</h3>
            <p>
              At Pawsible, our shuttle service doesn’t end with the last drop-off. <span class="highlight">End-of-day reporting ensures accountability, identifies issues, and keeps all teams aligned</span> for the next morning's success.
            </p>
          </section>
      
          <section>
            <h4>🗂️ 1. Complete Your Route Recap Log</h4>
            <ul>
              <li>✅ Mark all pickups and drop-offs completed in the Pawsible driver task board</li>
              <li>📍 Note any skipped or rescheduled stops (with client reason if available)</li>
              <li>📦 List all items left in van (leashes, crates, gear)</li>
              <li>🚨 Report any notable incidents (dog stress, reroutes, delays, equipment issue)</li>
            </ul>
            <p><span class="highlight-blue">Your log is a handoff to the next shift — make sure the story is complete.</span></p>
          </section>
      
          <section>
            <h4>🧼 2. Van Reset for Tomorrow</h4>
            <ul>
              <li>🧹 Sweep van floor and remove all debris, fur, or drool</li>
              <li>🧴 Sanitize all seat areas and surfaces (inside doors, mats, etc.)</li>
              <li>🚫 Remove all trash, old pads, or used towels</li>
              <li>🧺 Refill or replace supplies (fresh pads, spray, paper towels)</li>
            </ul>
            <p><span class="highlight-green">A fresh van shows pride and sets the tone for a smooth, respectful start the next day.</span></p>
          </section>
      
          <section>
            <h4>📲 3. Submit Notes to Shuttle Manager</h4>
            <ul>
              <li>📩 Message or log anything unusual that may impact tomorrow’s schedule (e.g., dog refused loading, leash snapped, family moved)</li>
              <li>📷 Optional: Share images of any damage, mess, or client concern for documentation</li>
              <li>📆 If vehicle needs service, low on gas, or supply restock — flag it immediately</li>
            </ul>
            <p><span class="highlight-yellow">Transparency at the end of your shift prevents confusion at the start of someone else’s.</span></p>
          </section>
      
          <section>
            <h4>🌱 Final Thought</h4>
            <p>
              <span class="highlight">Your final task is your team’s first support.</span> 
              Closing the day with clear documentation and a clean van sets the tone for a well-oiled operation — and for more tail wags tomorrow.
            </p>
          </section>
        `
      }      
      
}